import { ReactNode, useMemo, useState } from 'react';

import { ProductCatalogMap, ProductDetails } from '@common/models';
import createTypeSafeContext from '@core/utils/createTypeSafeContext';

import { useProductsAsProductDetails } from './useProductsAsProductDetails';

interface ProductDetailsContextValue {
    productCatalog: ProductCatalogMap;
    restorableCatalog: ProductCatalogMap;
    setRestorableCatalog: (value: ProductCatalogMap) => void;
}

export const [useProductDetailsContext, ProductDetailsContext] = createTypeSafeContext<ProductDetailsContextValue>();

export const ProductDetailsContextProvider = ({ children }: { children?: ReactNode }): JSX.Element => {
    const [restorableCatalog, setRestorableCatalog] = useState<ProductCatalogMap>(new Map());

    /**
     * this gets all the various productDetails lists from a central place
     * hopefully we can eliminate all of this in the future
     */
    const {
        colorsAsProductDetails,
        cookingAppliancesAsProductDetails,
        cornerRadiiAsProductDetails,
        cutoutLaborAsProductDetails,
        diagonalCornersAsProductDetails,
        dishwashersAsProductDetails,
        edgesAsProductDetails,
        cutoutsAsProductDetails,
        tearoutsAsProductDetails,
        chipMinimizerAsProductDetails,
        customerSuppliedSinksAsProductDetails,
        faucetsAsProductDetails,
        optionalProductsAsProductDetails,
        refrigeratorsAsProductDetails,
        sinksAsProductDetails,
        waterfallLaborAsProductDetails,
        plumbingAsProductDetails,
        endCapsAsProductDetails,
        endSplashesAsProductDetails,
    } = useProductsAsProductDetails();

    const productCatalog = useMemo(() => {
        const productMap = new Map(restorableCatalog);
        [
            chipMinimizerAsProductDetails,
            colorsAsProductDetails,
            cookingAppliancesAsProductDetails,
            cornerRadiiAsProductDetails,
            customerSuppliedSinksAsProductDetails, // customerSuppliedSinks MUST come before cutouts and cutoutLabor in this list!!!!
            cutoutsAsProductDetails, // keep cutouts before cutoutLabor, although it shouldn't matter
            cutoutLaborAsProductDetails,
            diagonalCornersAsProductDetails,
            dishwashersAsProductDetails,
            edgesAsProductDetails,
            faucetsAsProductDetails,
            optionalProductsAsProductDetails,
            refrigeratorsAsProductDetails,
            sinksAsProductDetails,
            tearoutsAsProductDetails,
            waterfallLaborAsProductDetails,
            plumbingAsProductDetails,
            endCapsAsProductDetails,
            endSplashesAsProductDetails,
        ].forEach((products) => {
            products.forEach((product: ProductDetails) => {
                productMap.set(product.productId, product);
            });
        });
        return productMap;
    }, [
        chipMinimizerAsProductDetails,
        colorsAsProductDetails,
        cookingAppliancesAsProductDetails,
        cornerRadiiAsProductDetails,
        customerSuppliedSinksAsProductDetails,
        cutoutLaborAsProductDetails,
        cutoutsAsProductDetails,
        diagonalCornersAsProductDetails,
        dishwashersAsProductDetails,
        edgesAsProductDetails,
        faucetsAsProductDetails,
        optionalProductsAsProductDetails,
        refrigeratorsAsProductDetails,
        restorableCatalog,
        sinksAsProductDetails,
        tearoutsAsProductDetails,
        waterfallLaborAsProductDetails,
        plumbingAsProductDetails,
        endCapsAsProductDetails,
        endSplashesAsProductDetails,
    ]);

    return (
        <ProductDetailsContext.Provider
            value={{
                productCatalog,
                restorableCatalog,
                setRestorableCatalog,
            }}
        >
            {children}
        </ProductDetailsContext.Provider>
    );
};
